// src/components/ProductSearchPanel.js
//
// Panel de búsqueda rápida de productos para el módulo Menú del POS.
// Filtra en memoria sobre el array de productos ya cargado (mismo array que
// usan GroupList/CategoryList/ProductList), sin llamadas de red adicionales.
// Al seleccionar un producto invoca onSelectProduct(product), la MISMA
// función (handleProductSelection) que usa la navegación normal por
// categorías, para no duplicar ninguna lógica de precio/impuestos/
// modificadores/promociones.
import React, { useState, useEffect, useMemo, useRef } from "react";
import "../styles/ProductSearchPanel.css";

const MAX_RESULTS = 60;
const DEBOUNCE_MS = 220;

const normalizeText = (value) => {
  if (value === undefined || value === null) return "";
  return String(value)
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "") // quitar acentos
    .toLowerCase()
    .trim();
};

const ProductSearchPanel = ({ products = [], onSelectProduct, onClose }) => {
  const [query, setQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  const [addedIds, setAddedIds] = useState(() => new Set());
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const addedTimersRef = useRef({});

  // Autofocus al abrir (hereda el teclado en pantalla global vía focusin)
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Cerrar con Escape
  useEffect(() => {
    const handler = (e) => {
      if (e.key === "Escape") onClose && onClose();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  // Debounce de la búsqueda para no filtrar en cada tecla sin control
  useEffect(() => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      setDebouncedQuery(query);
    }, DEBOUNCE_MS);
    return () => clearTimeout(debounceTimerRef.current);
  }, [query]);

  // Limpiar timers de "agregado" al desmontar
  useEffect(() => {
    return () => {
      Object.values(addedTimersRef.current).forEach((t) => clearTimeout(t));
    };
  }, []);

  const normalizedQuery = normalizeText(debouncedQuery);

  const results = useMemo(() => {
    if (!Array.isArray(products) || !normalizedQuery) return [];
    const matches = [];
    for (let i = 0; i < products.length; i++) {
      const product = products[i];
      if (!product || !product.name) continue;
      if (normalizeText(product.name).includes(normalizedQuery)) {
        matches.push(product);
        if (matches.length >= MAX_RESULTS) break;
      }
    }
    return matches;
  }, [products, normalizedQuery]);

  const hasMorePossibleResults = useMemo(() => {
    if (!Array.isArray(products) || !normalizedQuery) return false;
    if (results.length < MAX_RESULTS) return false;
    // Solo informativo: puede haber más de MAX_RESULTS coincidencias
    return products.filter(
      (p) => p && p.name && normalizeText(p.name).includes(normalizedQuery)
    ).length > MAX_RESULTS;
  }, [products, normalizedQuery, results.length]);

  const handleSelect = (product) => {
    if (!product || typeof onSelectProduct !== "function") return;

    // Reutiliza exactamente el mismo flujo de selección que la navegación
    // por categorías (precio, impuestos, cargo, modificadores, notas...).
    onSelectProduct(product);

    // Feedback visual breve de "agregado" antes de cerrar el panel.
    const key = product.id ?? product.name;
    setAddedIds((prev) => new Set(prev).add(key));

    // Cerrar el panel tras la selección: evita solapamientos con otros
    // modales que handleProductSelection pueda abrir (modificadores, nota).
    if (addedTimersRef.current[key]) clearTimeout(addedTimersRef.current[key]);
    addedTimersRef.current[key] = setTimeout(() => {
      onClose && onClose();
    }, 180);
  };

  return (
    <div
      className="psp-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose && onClose();
      }}
    >
      <div className="psp-panel">
        <div className="psp-header">
          <i className="bi bi-search" />
          <input
            ref={inputRef}
            type="text"
            className="psp-input"
            placeholder="Buscar producto por nombre..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="button"
            className="psp-close-btn"
            onClick={() => onClose && onClose()}
            aria-label="Cerrar búsqueda"
          >
            ✕
          </button>
        </div>

        <div className="psp-body">
          {normalizedQuery === "" && (
            <p className="psp-hint">Escribe el nombre del producto para buscar.</p>
          )}

          {normalizedQuery !== "" && results.length === 0 && (
            <p className="psp-empty">No se encontraron productos para "{query}".</p>
          )}

          {results.length > 0 && (
            <ul className="psp-results-list">
              {results.map((product, idx) => {
                const key = product.id ?? `${product.name}-${idx}`;
                const isAdded = addedIds.has(product.id ?? product.name);
                return (
                  <li
                    key={key}
                    className={`psp-result-item${isAdded ? " psp-result-added" : ""}`}
                    onClick={() => handleSelect(product)}
                  >
                    <div>
                      <div className="psp-result-name">{product.name}</div>
                      {(product.category || product.group) && (
                        <div className="psp-result-meta">
                          {[product.category, product.group].filter(Boolean).join(" · ")}
                        </div>
                      )}
                    </div>
                    {isAdded ? (
                      <span className="psp-result-added-badge">✓ Agregado</span>
                    ) : (
                      typeof product.price === "number" && (
                        <span className="psp-result-price">${product.price.toFixed(2)}</span>
                      )
                    )}
                  </li>
                );
              })}
            </ul>
          )}

          {hasMorePossibleResults && (
            <div className="psp-more-results">
              Mostrando los primeros {MAX_RESULTS} resultados. Sigue escribiendo para afinar la búsqueda.
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ProductSearchPanel;
